import { Component, Host, h, Listen, State, Element, Prop, Event, EventEmitter } from '@stencil/core';
import { $$$ } from '../../../utils/utils';
import { HamburgerEvent } from '../../01_atoms/lilu-hamburger/HamburgerEvent';

export type HeaderMode = undefined | 'compact';

@Component({
  tag: 'lilu-header',
  styleUrl: 'lilu-header.scss',
  shadow: true,
})
export class LiluHeader {
  @Element() host: HTMLElement;

  @Prop() headerMode: HeaderMode;
  @Prop() ajaxSearchUrl: string;
  @Prop() dataSearchUrl: string;

  @State() isMenuOpen = false;
  @State() isBodyAtTop = false;
  @State() disturberHeight: number;
  @State() bodyToTop: number;

  @Event({
    eventName: 'headerEventEmitter',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  headerEventEmitter: EventEmitter<HamburgerEvent>;

  componentWillLoad() {
    this.makePageDivVisible();
    this.isBodyAtTop = window.scrollY === 0;
  }

  componentDidLoad() {
    const resizeObserver = new ResizeObserver(_ => {
      this.getPageWrapper().style.paddingTop = this.getHeaderHeight() + 'px';
      const navigationTop = this.getHeaderHeight() - this.getDisturberHeight();
      if (!this.headerIsCompact) {
        this.getNavigationContainer().style.top = navigationTop + 'px';
      }
    });

    resizeObserver.observe(this.host.shadowRoot.querySelector('header'));
  }

  makePageDivVisible() {
    document.body.style.visibility = 'visible';
  }

  // Listener
  @Listen('hamburgerEventEmitter')
  handleFunction(event: CustomEvent<HamburgerEvent>) {
    this.isMenuOpen = event.detail.isOpen;
    const pageWrapperClassList = document.querySelector('.page-wrapper').classList;

    if (this.isMenuOpen) {
      document.documentElement.style.setProperty('--scroll-y', `-${window.scrollY}px`); // store scroll position. All logic is in css 'isHamburgerClicked' class
      document.documentElement.style.setProperty('--left-pos', `${document.querySelector('.page-wrapper').getBoundingClientRect().left}px`);

      pageWrapperClassList.add('isHamburgerClicked');
    } else {
      pageWrapperClassList.remove('isHamburgerClicked');
      const scrollPos = parseInt(document.documentElement.style.getPropertyValue('--scroll-y') || '0') * -1;
      window.scrollTo(0, scrollPos); // restore scroll position
      $$$('lilu-navigation')[0].resetNavigationState();
    }
  }

  @Listen('scroll', { target: 'window' })
  onScroll() {
    this.isBodyAtTop = window.scrollY === 0;
  }

  @Listen('click', { target: 'window' })
  clickOutsideOfComponent(event) {
    // Do nothing when component receives a click inide or menu is closed
    if (!this.isMenuOpen || event.composedPath().includes(this.host)) {
      return;
    }

    this.isMenuOpen = false;
    $$$('lilu-navigation')[0].resetNavigationState();
    const pageWrapperClassList = document.querySelector('.page-wrapper').classList;
    pageWrapperClassList.remove('isHamburgerClicked');
    const scrollPos = parseInt(document.documentElement.style.getPropertyValue('--scroll-y') || '0') * -1;
    window.scrollTo(0, scrollPos); // restore scroll position
  }

  getHeaderHeight(): number {
    return this.host.shadowRoot.querySelector('header').clientHeight;
  }

  getDisturberHeight(): number {
    const liluDisturber = document.querySelector('lilu-disturber');
    if (liluDisturber) {
      return liluDisturber.clientHeight;
    } else {
      return 0;
    }
  }

  getPageWrapper(): HTMLElement {
    return document.querySelector('.page-wrapper');
  }

  getNavigationContainer(): HTMLElement {
    return this.host.shadowRoot.querySelector('.navigation_container');
  }

  get headerIsCompact() {
    return this.headerMode === 'compact';
  }

  render() {
    const isCompact = this.headerIsCompact;

    const headerClasses = ['header-sticky', !this.isBodyAtTop ? 'small-header' : '', this.isMenuOpen ? 'isMenuOpen' : '', isCompact ? 'compact-header' : ''].join(' ');

    return (
      <Host>
        <header class={headerClasses}>
          <div class="header">
            <div class="header__col header__col--left">
              {!isCompact && (
                <div class="header__hamburger">
                  <lilu-hamburger is-hamburger-clicked={this.isMenuOpen}></lilu-hamburger>
                </div>
              )}
              <div class="header__logo">
                <slot name="logo"></slot>
              </div>
              {!isCompact && (
                <div class="header__nav-quick">
                  <slot name="nav-quick"></slot>
                </div>
              )}
              {!isCompact && (
                <div class="header__search-form">
                  <lilu-search ajax-url={this.ajaxSearchUrl} data-search-url={this.dataSearchUrl}></lilu-search>
                </div>
              )}
            </div>
            <div class="header__col header__col--right">
              <slot name="nav-col"></slot>
            </div>
          </div>
          <slot name="disturber"></slot>
          {!isCompact && (
            <div class={`navigation_container ${this.isMenuOpen ? 'isMenuOpen' : ''}`}>
              <slot name="navigation"></slot>
            </div>
          )}
        </header>
      </Host>
    );
  }
}
